import React, { useState, useEffect } from "react";
import { Send, Filter, Users, MessageCircle, Phone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { StatusBadge } from "../components/common/StatusBadge";
import { apiRequest, formatPhone, formatDate } from "../lib/utils";
import { toast } from "sonner";

export const NoShowCampaignPage = () => {
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  
  // Filters
  const [branchFilter, setBranchFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // Campaign form
  const [campaign, setCampaign] = useState({
    message: "",
    offer_type: "discount",
    channel: "whatsapp"
  });

  const offerTemplates = {
    discount: "Dear {{name}}, we miss you! Get 10% OFF on your next service. Book now and enjoy exclusive savings. Call us at {{branch_phone}} or reply to this message.",
    free_pickup: "Dear {{name}}, we miss you! Enjoy FREE pickup & drop for your next service. We'll come to you! Book now at {{branch_phone}}.",
    free_check: "Dear {{name}}, we miss you! Get a FREE health check-up for your vehicle. No obligations, just care. Call us at {{branch_phone}}."
  };

  useEffect(() => {
    fetchNoShowCustomers();
    fetchBranches();
  }, [branchFilter, dateFrom, dateTo]);

  const fetchNoShowCustomers = async () => {
    setLoading(true);
    try {
      let url = "/customers/no-show/list";
      const params = [];
      if (branchFilter && branchFilter !== "all") {
        params.push(`branch_id=${branchFilter}`);
      }
      if (dateFrom) {
        params.push(`date_from=${dateFrom}`);
      }
      if (dateTo) {
        params.push(`date_to=${dateTo}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const data = await apiRequest(url);
      setCustomers(data);
    } catch (error) {
      toast.error("Failed to fetch no-show customers");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCustomers(customers.map(c => c.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleSelectCustomer = (customerId, checked) => {
    if (checked) {
      setSelectedCustomers([...selectedCustomers, customerId]);
    } else {
      setSelectedCustomers(selectedCustomers.filter(id => id !== customerId));
    }
  };

  const handleOfferTypeChange = (type) => {
    setCampaign({
      ...campaign,
      offer_type: type,
      message: offerTemplates[type]
    });
  };

  const handleSendCampaign = async () => {
    if (selectedCustomers.length === 0) {
      toast.error("Please select at least one customer");
      return;
    }
    if (!campaign.message) {
      toast.error("Please enter a message");
      return;
    }

    try {
      const response = await apiRequest("/campaigns/no-show", {
        method: "POST",
        body: JSON.stringify({
          customer_ids: selectedCustomers,
          message: campaign.message,
          offer_type: campaign.offer_type,
          channel: campaign.channel
        })
      });
      toast.success(response.message);
      setShowCampaignModal(false);
      setSelectedCustomers([]);
      setCampaign({ message: "", offer_type: "discount", channel: "whatsapp" });
    } catch (error) {
      toast.error(error.message || "Failed to send campaign");
    }
  };

  return (
    <div className="space-y-6" data-testid="no-show-campaign-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">No Show Campaign</h1>
          <p className="text-muted-foreground">Send offers to bring back no-show customers</p>
        </div>
        <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
          <DialogTrigger asChild>
            <Button disabled={selectedCustomers.length === 0} data-testid="send-campaign-btn">
              <Send className="w-4 h-4 mr-2" />
              Send Offer ({selectedCustomers.length})
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Send Campaign</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <Users className="w-4 h-4 inline mr-2" />
                Sending to <strong>{selectedCustomers.length}</strong> customers
              </div>

              <div>
                <Label>Offer Type</Label>
                <Select value={campaign.offer_type} onValueChange={handleOfferTypeChange}>
                  <SelectTrigger data-testid="offer-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount">10% Discount</SelectItem>
                    <SelectItem value="free_pickup">Free Pickup & Drop</SelectItem>
                    <SelectItem value="free_check">Free Vehicle Check</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Channel</Label>
                <Select value={campaign.channel} onValueChange={(v) => setCampaign({ ...campaign, channel: v })}>
                  <SelectTrigger data-testid="channel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp
                      </div>
                    </SelectItem>
                    <SelectItem value="sms">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        SMS
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Message</Label>
                <Textarea
                  value={campaign.message}
                  onChange={(e) => setCampaign({ ...campaign, message: e.target.value })}
                  rows={5}
                  placeholder="Enter your offer message..."
                  data-testid="campaign-message"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use {"{{name}}"} for customer name, {"{{branch_phone}}"} for branch contact
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowCampaignModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSendCampaign} data-testid="confirm-send-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Send Campaign
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bento-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground">Branch</Label>
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger data-testid="branch-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bento-card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold font-heading text-red-600">{customers.length}</p>
            <p className="text-sm text-muted-foreground">Total No Show</p>
          </CardContent>
        </Card>
        <Card className="bento-card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold font-heading text-blue-600">{selectedCustomers.length}</p>
            <p className="text-sm text-muted-foreground">Selected</p>
          </CardContent>
        </Card>
        <Card className="bento-card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold font-heading text-green-600">{branches.length}</p>
            <p className="text-sm text-muted-foreground">Branches</p>
          </CardContent>
        </Card>
        <Card className="bento-card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold font-heading text-purple-600">
              {[...new Set(customers.map(c => c.branch_id))].length}
            </p>
            <p className="text-sm text-muted-foreground">Branches Affected</p>
          </CardContent>
        </Card>
      </div>

      {/* Customers Table */}
      <Card className="bento-card">
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={selectedCustomers.length === customers.length && customers.length > 0}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No no-show customers found
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer) => (
                    <TableRow key={customer.id} className="table-high-density" data-testid={`customer-row-${customer.id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedCustomers.includes(customer.id)}
                          onCheckedChange={(checked) => handleSelectCustomer(customer.id, checked)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{customer.name}</TableCell>
                      <TableCell className="font-mono text-sm">{formatPhone(customer.phone)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {customer.branch_name || "-"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={customer.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(customer.updated_at)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NoShowCampaignPage;
